/** @typedef {import('headlight-configurable-application').Pict} Pict */

/** @type {typeof import('pict-section-form').PictInputExtensionProvider} */
const PictSectionInputExtension_TabGroupSelector = require('pict-section-form/source/providers/inputs/Pict-Provider-Input-TabGroupSelector.js');

const libTabPrintHostCSS = require('./_TabPrintHostCSS.js');

class TabGroupSelectorInputHandler extends PictSectionInputExtension_TabGroupSelector
{
	/**
	 * @param {Object} pFable - The Fable instance.
	 * @param {Object} [pOptions] - The provider options.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/**
		 * Last tab the user actually selected, keyed `<viewHash>::<inputHash>`.
		 *
		 * WHY THIS EXISTS: the base provider restores the active tab purely from the
		 * input's DATA value, so ANY path that blanks it drops the user back to the
		 * default tab. On the LADOTD Sample Creation form that surfaced as "every time
		 * I manually add a test it switches tabs back" -- adding a test fires an
		 * AutofillTriggerGroup whose PostSolvers re-render the section, and the tab
		 * would snap to `DefaultTabGroupHash` mid-edit. It also took the select2
		 * dropdown down with it (the widget's element is detached by the re-render,
		 * so a subsequent open threw "Cannot read properties of null (reading 'query')").
		 *
		 * Deliberately in-memory only: this is UI position, not form data. It is NOT a
		 * replacement for the stored value -- selectTabByViewHash still writes that, so
		 * the tab survives a genuine reload. This only wins when the two disagree,
		 * which is exactly the re-render case.
		 *
		 * @type {Object<string, string>}
		 */
		this.SelectedTabByInput = {};

		// Shared with TabSectionSelector -- see _TabPrintHostCSS.js for why this registers here and
		// injects at init rather than doing both in the constructor.
		libTabPrintHostCSS.registerPrintHostCSS(this.pict);
	}

	/**
	 * @param {string} pViewHash
	 * @param {string} pInputHash
	 * @return {string}
	 */
	getSelectedTabKey(pViewHash, pInputHash)
	{
		return `${pViewHash}::${pInputHash}`;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLTabSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @returns {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLTabSelector, pTransactionGUID)
	{
		// Before the print early-return on purpose: the print-host rule is exactly what the PDF needs.
		libTabPrintHostCSS.injectPrintHostCSS(this.pict);

		if (!this.pict.AppData.Interactive && pInput.PictForm?.Exclusive !== true)
		{
			// show all tabs in print mode unless Exclusive is set to true
			return;
		}

		// Prefer the tab the user last picked over whatever is in the data value, so a
		// re-render mid-edit cannot yank them back to the default tab. Fed in as pValue
		// so the base provider's precedence (DefaultFromData -> DefaultTabGroupHash ->
		// first tab) is untouched; it also re-writes the value, repairing the data.
		// Honours an explicit `DefaultFromData: false`, which means "always start on the
		// configured default" and is nobody's idea of a remembered tab.
		let tmpValue = pValue;
		if (pInput?.PictForm?.DefaultFromData !== false)
		{
			const tmpRemembered = this.SelectedTabByInput[this.getSelectedTabKey(pView?.Hash, pInput?.Hash)];
			// Only if it is still a real member of this input's tab set -- the manifest
			// can change under a long-lived page.
			if (tmpRemembered && Array.isArray(pInput?.PictForm?.TabGroupSet) && pInput.PictForm.TabGroupSet.includes(tmpRemembered))
			{
				tmpValue = tmpRemembered;
			}
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, tmpValue, pHTMLTabSelector, pTransactionGUID);
	}

	/**
	 * Tier-3 (Half A) a11y: after the base provider swaps the visible tab/group
	 * via CSS classes, mirror the same state into ARIA so assistive tech
	 * announces the tabs as a real tablist. We deliberately do NOT add roving
	 * tabindex or arrow-key navigation (Half B) — every tab stays natively
	 * Tab-focusable, which is already WCAG 2.x keyboard-operable.
	 *
	 * `role="tablist"`/`role="tab"`/`role="presentation"` are emitted statically
	 * by templates/InputType-TabGroupSelector.js; only the stateful bits
	 * (aria-selected) and the cross-element relationships (tab <-> group panel,
	 * whose id carries the runtime view formID) are set here. Idempotent: runs
	 * once on init and again on every tab switch.
	 *
	 * @param {string} pViewHash
	 * @param {string} pInputHash
	 * @param {string} pTabHash
	 *
	 * @return {boolean}
	 */
	selectTabByViewHash(pViewHash, pInputHash, pTabHash)
	{
		const tmpResult = super.selectTabByViewHash(pViewHash, pInputHash, pTabHash);
		if (tmpResult === false)
		{
			return tmpResult;
		}

		// Remember the selection so a later re-render restores it (see the constructor).
		this.SelectedTabByInput[this.getSelectedTabKey(pViewHash, pInputHash)] = pTabHash;

		const tmpView = this.pict.views[pViewHash];
		const tmpInput = tmpView ? tmpView.getInputFromHash(pInputHash) : null;
		if (!tmpInput || !Array.isArray(tmpInput?.PictForm?.TabGroupSet))
		{
			return tmpResult;
		}

		for (let i = 0; i < tmpInput.PictForm.TabGroupSet.length; i++)
		{
			const tmpTabGroupHash = tmpInput.PictForm.TabGroupSet[i];
			const tmpTabID = `TAB-${tmpTabGroupHash}-${tmpInput.Macro.RawHTMLID}`;
			const tmpTabSelector = `#${tmpTabID} a`;
			const tmpPanelSelector = this.getGroupSelector(tmpView, tmpTabGroupHash);
			const tmpIsActive = (tmpTabGroupHash === pTabHash);

			// State: the active tab is the selected one.
			this.pict.ContentAssignment.setAttribute(tmpTabSelector, 'aria-selected', tmpIsActive ? 'true' : 'false');
			// Relationship: tab controls its group panel; panel is a tabpanel labelled by its tab.
			this.pict.ContentAssignment.setAttribute(tmpTabSelector, 'aria-controls', tmpPanelSelector.replace(/^#/, ''));
			this.pict.ContentAssignment.setAttribute(tmpPanelSelector, 'role', 'tabpanel');
			this.pict.ContentAssignment.setAttribute(tmpPanelSelector, 'aria-labelledby', tmpTabID);
		}

		return tmpResult;
	}
}

module.exports = TabGroupSelectorInputHandler;
